import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Label,
  Input,
  Button,
  Spinner,
  InputGroup,
} from "reactstrap";
import { Period } from "modules/Clinical/TimePoints/helpers/timpoints.types";
import { formatDate } from "common/service/CommonService";

interface SingleRowVerifyModalProps {
  isOpen: boolean;
  toggle: () => void;
  verifyData: any;
  modalPeriod: Period | null;
  onSubmitPin: (pin: string) => void;
  isPending: boolean;
}

const SingleRowVerifyModal: React.FC<SingleRowVerifyModalProps> = ({
  isOpen,
  toggle,
  verifyData,
  modalPeriod,
  onSubmitPin,
  isPending,
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setShowPassword(false);
      setAck(false);
    }
  }, [isOpen]);

  if (!verifyData) return null;

  const handleSubmit = () => {
    if (!password.trim() || !ack) return;
    onSubmitPin(password);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="bg-light border-bottom p-3">
        <div className="d-flex align-items-center gap-2">
          <div className="avatar-xs flex-shrink-0">
            <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-15">
              <i className="ri-shield-keyhole-line"></i>
            </span>
          </div>
          <div>
            <h6 className="mb-0 fw-bold fs-14">Verify Sample Collection Time Correction</h6>
            <p className="text-muted fs-11 mb-0">Single Timepoint Verification</p>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="p-3">
        <div className="bg-light p-3 rounded-3 border mb-3">
          <Row className="g-2 mb-2 fs-12">
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Period</span>
              <strong className="text-dark">
                {modalPeriod?.period_name ||
                  modalPeriod?.periodname ||
                  `Period ${verifyData.period_number || modalPeriod?.period_number}`}
              </strong>
            </Col>
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Time Point</span>
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-0.5 fw-bold fs-11">
                {verifyData.timepoint_number || "--"}
              </span>
            </Col>
          </Row>

          <Row className="g-2 mb-2 border-top pt-2 fs-12">
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Previous Time</span>
              <span className="fw-bold text-danger">
                {verifyData.previous_collection_time || "--"}
              </span>
            </Col>
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">New Time</span>
              <span className="fw-bold text-success">
                {verifyData.new_collection_time || verifyData.corrected_collection_time || "--"}
              </span>
            </Col>
          </Row>

          <Row className="g-2 border-top pt-2 fs-12">
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Corrected By</span>
              <strong className="text-primary fs-12">
                <i className="ri-user-3-line me-1"></i>
                {verifyData.correctedbytext || verifyData.updatedbyname || verifyData.correctedby || "--"}
              </strong>
            </Col>
            <Col xs={6}>
              <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Corrected Date &amp; Time</span>
              <span className="text-dark fs-12">
                <i className="ri-time-line me-1"></i>
                {formatDate(verifyData.correcteddate || verifyData.updateddate) || "--"}
              </span>
            </Col>
          </Row>

          <div className="border-top pt-2 mt-2">
            <span className="text-muted d-block fs-11 text-uppercase fw-semibold">Reason for Correction</span>
            <div className="p-2 bg-white rounded border text-dark fs-12 mt-1 fw-medium" style={{ borderLeft: "3px solid #f1c40f" }}>
              "{verifyData.reason || verifyData.updatereason || "No reason provided"}"
            </div>
          </div>
        </div>

        <div className="mb-3">
          <Label className="form-label fw-semibold text-dark fs-12 mb-1">
            Enter Site Admin Security PIN <span className="text-danger">*</span>
          </Label>
          <InputGroup size="sm">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Enter PIN"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="fs-12"
            />
            <Button
              type="button"
              color="light"
              outline
              className="border"
              onClick={() => setShowPassword(!showPassword)}
            >
              <i className={`ri-eye-${showPassword ? "off-" : ""}line`}></i>
            </Button>
          </InputGroup>
        </div>

        <div className="p-2 bg-warning-subtle text-warning-emphasis rounded border border-warning-subtle fs-11 mb-1">
          <div className="form-check m-0 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input mt-0"
              id="rowVerifyAckCheck"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
            />
            <label className="form-check-label cursor-pointer text-dark fw-medium mb-0" htmlFor="rowVerifyAckCheck">
              I confirm that I have reviewed the correction reason and verify this timepoint.
            </label>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bg-white border-top p-2.5">
        <Button
          color="light"
          className="px-3 btn-sm border"
          onClick={toggle}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          className="px-3 btn-sm d-flex align-items-center gap-1"
          disabled={!password.trim() || !ack || isPending}
          onClick={handleSubmit}
        >
          {isPending ? (
            <>
              <Spinner size="sm" className="me-1" />
              Verifying...
            </>
          ) : (
            <>
              <i className="ri-checkbox-circle-line fs-14"></i>
              Verify Correction
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(SingleRowVerifyModal);
